import { MetaradarStateSnapshot } from '@app/model/metaradar/MetaradarStateSnapshot';
import _isEmpty from 'lodash/isEmpty';
import Message from '@app/component/message/Message';
import React, { useState } from 'react';
import Utilities from '@util/Utilities';
import { findProviderData } from '@app/component/provider/FindProviderData';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import MetaradarStateItem from '@app/model/metaradar/MetaradarStateItem';
import { ChevronDownIcon, ChevronUpDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';

enum SORT {
	ASC, DESC
}

const MetaradarDailyReportUndercutsAnalysisFragment: React.FunctionComponent<{
	metaradarStateSnapshot: MetaradarStateSnapshot
}> = ({ metaradarStateSnapshot }) => {

	const { companyHotel } = useSelectedCompanyHotel();
	const [sortPercentage, setSortPercentage] = useState<SORT | undefined>(SORT.DESC);

	if (_isEmpty(metaradarStateSnapshot.dailyItems)) {
		return <Message state="info">There are no results found!</Message>;
	}

	const undercutsPerStatDate = metaradarStateSnapshot.findUndercutsByStatDate(companyHotel.metaradarSettings?.threshold || 0.9);

	if (_isEmpty(undercutsPerStatDate)) {
		return <div className="p-6"><Message state="info">No results found!</Message></div>;
	}

	const sortUndercutsForPercentage = (undercuts: {
		brandOffer: MetaradarStateItem;
		statDate: string,
		difference: number,
		percentage: number,
		item: MetaradarStateItem
	}[]) => {
		if (sortPercentage === undefined) return undercuts;
		const sorted = [...undercuts];
		if (sortPercentage === SORT.ASC) {
			return sorted.sort((a, b) => a.percentage - b.percentage);
		} else {
			return sorted.sort((a, b) => b.percentage - a.percentage);
		}
	};

	return <div className="w-full shadow ring-1 ring-black ring-opacity-5">
		<div className="overflow-x-auto overflow-y-auto max-h-[70dvh]">
			<div className="inline-block min-w-full align-middle">
				<table className="min-w-full divide-y divide-gray-300">
					<thead className="sticky-table-header">
					<tr className="divide-x divide-gray-200">
						<th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Date</th>
						<th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Channel</th>
						<th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Region</th>
						<th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Brand Price</th>
						<th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Undercut Price</th>
						<th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Diff</th>
						<th scope="col" className="px-3 py-3 text-left text-sm font-semibold text-gray-900">
							<div className="flex items-center gap-x-1 cursor-pointer"
									 onClick={() => setSortPercentage(sortPercentage === undefined ? SORT.DESC : (sortPercentage === SORT.DESC ? SORT.ASC : undefined))}>
								<span>Diff %</span>
								{sortPercentage === undefined && <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />}
								{sortPercentage === SORT.ASC && <ChevronUpIcon className="h-4 w-4" />}
								{sortPercentage === SORT.DESC && <ChevronDownIcon className="h-4 w-4" />}
							</div>
						</th>
					</tr>
					</thead>
					<tbody className="divide-y divide-gray-200">

					{sortUndercutsForPercentage(undercutsPerStatDate).map(({ brandOffer, statDate, item, difference, percentage }) => {

						return <tr key={item.id} className={`divide-x divide-gray-200`}>
							<td
								className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">{Utilities.formatDateString(statDate, 'ddd DD/MM', false)}</td>
							<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
								<div className="flex items-center gap-x-2">
									<img className="block h-4 w-4" alt={findProviderData(item.provider).name}
											 title={findProviderData(item.provider).name}
											 src={`https://${item.iconUrl}`} />
									<span>{findProviderData(item.provider).name}</span>
								</div>
							</td>
							<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
								{Utilities.snakeCaseToWords(item.region)}
							</td>
							<td
								className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{Utilities.formatPrice(brandOffer.price)}</td>
							<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
								{Utilities.formatPrice(item.price)}
							</td>
							<td className="whitespace-nowrap px-3 py-4 text-sm">
								<span className="text-red-500">
									{Utilities.formatAmount(difference, item.price.currency)}
								</span>
							</td>
							<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
								{Utilities.formatDouble(percentage, 0)}%
							</td>
						</tr>;
					})}
					</tbody>
				</table>
			</div>
		</div>
	</div>;
};

export default MetaradarDailyReportUndercutsAnalysisFragment;